// The staff radio in the staff app (redesign 2026-09-27): one engine for the whole app, so it keeps
// running while staff move between tabs, shown as a slim bar on Now and a full screen on Radio.
// The engine is the same web or native (Android background service) engine the floating button
// used; nothing is recorded.
import { MIC_DISCLOSURE, useDisclosure } from "../safety/disclosure.tsx";
import { createContext, useContext, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { Button, Icon } from "@vavr/ui";
import { Radio, type RadioEngine, type RadioEngineFactory, type RadioOptions, type RadioState } from "@vavr/screens/shared";
import "@vavr/screens/shared/radio.css";

export interface RadioCtl {
  state: RadioState;
  on: boolean;
  speaker: boolean;
  background: boolean;
  status: string;
  start: () => void;
  stop: () => void;
  setSpeaker: (on: boolean) => void;
  /** Pointer and key handlers for a hold-to-talk button. */
  talk: {
    onPointerDown: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerUp: () => void; onPointerCancel: () => void; onLostPointerCapture: () => void;
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void; onKeyUp: (e: KeyboardEvent<HTMLButtonElement>) => void;
    onContextMenu: (e: { preventDefault: () => void }) => void;
  };
}

const RadioContext = createContext<RadioCtl | null>(null);
export const useRadio = () => useContext(RadioContext);

function statusOf(s: RadioState, background: boolean): string {
  switch (s.kind) {
    case "off": return "Radio off";
    case "connecting": return "Connecting";
    case "ready": return background ? "Hold to talk, or hold Volume Up" : "Hold to talk";
    case "requesting": return "Wait…";
    case "talking": return "On air";
    case "listening": return `${s.name} is talking`;
    case "busy": return `${s.name} is talking. Wait, then try again.`;
    case "error": return s.message;
  }
}

export function RadioProvider({ getTicket, wsBase, channelKey, engine, children }: {
  getTicket: RadioOptions["getTicket"]; wsBase: string; channelKey: string; engine?: RadioEngineFactory; children: ReactNode;
}) {
  const [state, setState] = useState<RadioState>({ kind: "off" });
  const [speaker, setSpeakerState] = useState(true);
  const radio = useRef<RadioEngine | null>(null);
  const disclose = useDisclosure(MIC_DISCLOSURE);
  const ticket = useRef(getTicket);
  ticket.current = getTicket;

  useEffect(() => {
    const o: RadioOptions = { getTicket: (pass) => ticket.current(pass), wsBase, onState: setState };
    const r = engine ? engine(o) : new Radio(o);
    radio.current = r;
    return () => { r.stop(); r.dispose(); radio.current = null; };
  }, [channelKey, wsBase, engine]);

  // The web engine never leaves the microphone open behind a backgrounded tab.
  useEffect(() => {
    if (engine) return;
    const hide = () => { if (document.visibilityState === "hidden") radio.current?.releaseButton(); };
    document.addEventListener("visibilitychange", hide);
    return () => document.removeEventListener("visibilitychange", hide);
  }, [engine]);

  const on = state.kind !== "off";
  const background = radio.current?.background ?? false;
  const ctl: RadioCtl = {
    state, on, speaker, background, status: statusOf(state, background),
    // The microphone is explained before the phone asks for it (PMU-238).
    start: () => void disclose.ask().then((ok) => { if (ok) void radio.current?.start(); }),
    stop: () => radio.current?.stop(),
    setSpeaker: (v) => { setSpeakerState(v); if (radio.current) radio.current.speaker = v; },
    talk: {
      onPointerDown: (e) => { if (!on) return; e.currentTarget.setPointerCapture(e.pointerId); e.preventDefault(); radio.current?.press(); },
      onPointerUp: () => radio.current?.releaseButton(),
      onPointerCancel: () => radio.current?.releaseButton(),
      onLostPointerCapture: () => radio.current?.releaseButton(),
      onKeyDown: (e) => { if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); radio.current?.press(); } },
      onKeyUp: (e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); radio.current?.releaseButton(); } },
      onContextMenu: (e) => e.preventDefault(),
    },
  };
  return <RadioContext.Provider value={ctl}>{disclose.dialog}{children}</RadioContext.Provider>;
}

const talkDisabled = (s: RadioState) => s.kind === "connecting" || s.kind === "listening";

/** The slim bar above the tabs on Now: who is talking, and a talk button. */
export function RadioBar({ onOpen }: { onOpen: () => void }) {
  const r = useRadio();
  if (!r) return null;
  return (
    <div className="pmt-radiobar" data-state={r.state.kind}>
      <button type="button" className="pmt-radiobar__text" onClick={onOpen} aria-label="Open the radio">
        <span className="pmt-radiobar__icon" aria-hidden="true"><Icon name="mic" /></span>
        <span className="pmt-radiobar__label"><b>Staff radio</b><small>{r.on ? r.status : "Off · turn on to hear staff"}</small></span>
      </button>
      {r.on ? (
        <button type="button" className="pmt-radiobar__talk" aria-label="Hold to talk on the staff radio" disabled={talkDisabled(r.state)} {...r.talk}><Icon name="mic" /></button>
      ) : (
        <button type="button" className="pmt-radiobar__on" onClick={r.start}>Turn on</button>
      )}
    </div>
  );
}

/** The Radio tab: one big button, the speaker and on/off. */
export function RadioPanel() {
  const r = useRadio();
  if (!r) return null;
  return (
    <div className="pmt-radio" data-state={r.state.kind}>
      {r.on ? (
        <button type="button" className="pmt-radio__talk" aria-label={r.state.kind === "talking" ? "On air. Release to stop talking." : "Hold to talk on the staff radio"}
          disabled={talkDisabled(r.state)} {...r.talk}>
          <Icon name="mic" />
        </button>
      ) : (
        <button type="button" className="pmt-radio__talk pmt-radio__talk--off" onClick={r.start} aria-label="Turn on the staff radio"><Icon name="mic" /></button>
      )}
      <p className="pmt-radio__status" role="status" aria-live="polite">{r.on ? r.status : "Tap to turn the radio on"}</p>
      <p className="pmt-radio__hint">Everyone at the school with the radio on hears you live. Nothing is recorded.</p>
      {r.on && (
        <div className="pmt-radio__row">
          <Button variant="secondary" icon={r.speaker ? "speaker" : "close"} onClick={() => r.setSpeaker(!r.speaker)}>{r.speaker ? "Speaker on" : "Speaker off"}</Button>
          <Button variant="ghost" onClick={r.stop}>Turn off</Button>
        </div>
      )}
    </div>
  );
}
